<template>
  <div class="cell-content-container">
    <div style="text-align: left;" :style="{'width': parent === 'monitoring' ? '18%' : '25%'}">
      <Tooltip :disabled="parent === 'builder'" :content="`${formatVolume(totalValue)}`" placement="top">
        <Tag class="volume-total" type="info" effect="plain" size="small">
          <span style="color: #000;">{{ total }}</span> <span style="font-size: 10px;">{{ typeLabel }}</span>
        </Tag>
      </Tooltip>
    </div>
    <div class="cell-content volume-line" :style="{ width: volumeLineWidth }">
      <div v-for="(_volume, i) in visibleVolumes" :key="i" class="volume-label">
        <spinner v-if="isLoading" width="20" height="20" />
        <template v-else>
          <Tooltip :content="contentTooltip(_volume.label, formatVolume(_volume.value))" placement="top-end" :disabled="isLoading">
            <div class="l-flex-row l-align-center">
              <span v-if="parent === 'builder' && !isBoxFormula" :style="{ background: chartColors[_volume.key], width: '10px', height: '10px', borderRadius: '2px' }" />
              <template v-if="parent !== 'builder' || (parent === 'builder' && isBoxFormula)">
                <span class="formula-box__legend" :style="{ background: chartColors[_volume.key], marginRight: '5px' }">
                  <svg-icon :name="_volume.icon" :size="['app-volume', 'crm-id'].includes(_volume.icon) ? 'small' : 'medium'" />
                </span>
              </template>
              <div class="volume-text">
                {{ formatVolume(_volume.value, true) }}
              </div>
            </div>
          </Tooltip>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { formatNumber, formatNumberIntl } from '@/utils/helpers'
import { isNil } from 'lodash'
import { Tooltip, Tag } from 'element-ui'
import { xDeviceVolumes, idBasedVolumes } from '@/utils/volumeOrder'
import { VOLUME_CHART_COLORS } from '@/utils/constants'

export default {
    name: 'CellVolume',

    components: {
        Tooltip,
        Tag,
    },

    props: {
        clickOnCell: {
            type: Function,
            default: () => {}
        },
        data: {
            type: [Function, Array, String, Number],
            default: null
        },
        type: {
            type: String,
            default: ''
        },
        fullData: {
            type: Object,
            default: () => ({})
        },
        parent: {
            type: String,
            default: ''
        },
        isBoxFormula: {
            type: Boolean,
            default: false,
            required: false
        }
    },

    data() {
        return {
            emptyVolumes: {
                total_doc: 0,
                total_mobile: 0,
                total_crm: 0,
                total_desktop: 0,
                total_desktop_first: 0,
                total_firstid: 0,
                total_xdevice: {
                    total_users: 0,
                    total_users_mobile: 0,
                    total_users_crm: 0,
                    total_users_desktop: 0,
                    total_users_desktop_first: 0,
                    total_users_firstid: 0
                }
            },

            chartColors: VOLUME_CHART_COLORS
        }
    },

    computed: {
        ...mapState('ui/monitoring', {
            typeVolume: state => state.typeVolume
        }),

        ...mapState('ui/settings', {
            firstPartyCookie: state => state.general.firstPartyCookie,
            providerFirstIdActivated: state => state.general.providerFirstIdActivated,
            providerUtiqActivated: state => state.general.providerUtiqActivated,
        }),

        ...mapState('builder', {
            uiBuilderVolumeType: state => state.formulaVolumeType
        }),

        ...mapState('account', {
            xDeviceInfo: state => state.xDeviceInfo
        }),

        isLoading() {
            if (this.parent === 'builder') return this.$store.state.builder.formulaVolumesLoading
            return this.$store.getters['ui/monitoring/audienceVolumeIsLoadingById'](this.fullData.id)
        },

        isCrossDevice() {
            if (this.parent === 'builder') return this.uiBuilderVolumeType === 'Cross device'
            return this.type === 'crossdevice'
        },

        isCrossDeviceIsActive() {
            return this.xDeviceInfo && this.xDeviceInfo.status === 'active'
        },

        isIdBased() {
            if (this.parent === 'builder') return this.uiBuilderVolumeType === 'ID based'
            return this.type === 'idbased'
        },

        volumes() {
            if (this.parent === 'builder') {
                if (!this.isBoxFormula) return !isNil(this.fullData?.volume) ? this.fullData.volume : this.emptyVolumes
                else {
                    if (this.fullData.operator === 'union') {
                        return !isNil(this.fullData?.volume) ? this.fullData.volume : this.emptyVolumes
                    } else {
                        return !isNil(this.fullData?.children?.[0]?.volume) ? this.fullData?.children?.[0]?.volume : this.emptyVolumes
                    }
                }
            }

            return !isNil(this.fullData.volumes) ? this.fullData.volumes : this.emptyVolumes
        },

        fixedWidth() {
            if (this.parent !== 'builder') return ''
            const boxHeader = document.querySelector('.formula-block-header')
            let width = 90
            let toExtract = 0
            if (boxHeader && boxHeader?.offsetWidth) {
                const _w = boxHeader?.offsetWidth
                width = (_w - (_w * 0.3) - (this.visibleVolumes.length * 10)) / this.visibleVolumes.length
                width = Math.round(width)
                if (this.isIdBased) {
                    toExtract = width
                }
            }
            return ({ 'max-width': (this.visibleVolumes.length * width - toExtract) + 'px' })
        },

        xDeviceVolumes() {
            return xDeviceVolumes(this.volumes, this.providerFirstIdActivated, this.firstPartyCookie, this.providerUtiqActivated)?.reverse()
        },

        idBasedVolumes() {
            return idBasedVolumes(this.volumes, this.providerFirstIdActivated, this.firstPartyCookie, this.providerUtiqActivated)?.reverse()
        },

        visibleVolumes() {
            return this.isCrossDevice && this.isCrossDeviceIsActive ? this.xDeviceVolumes : this.idBasedVolumes
        },

        totalValue() {
            let total = 0
            this.visibleVolumes.forEach(volume => {
                total += volume.value
            })
            return total
        },

        total() {
            return this.parent === 'monitoring' ? formatNumberIntl(this.totalValue) : this.formatVolume(this.totalValue)
        },

        typeLabel() {
            return this.isCrossDevice && this.isCrossDeviceIsActive ? 'Users' : 'IDs'
        },

        volumeLineWidth() {
            return this.parent === 'builder' ? `${(this.visibleVolumes.length * 10) + 10}%` : '90%'
        }
    },

    watch: {
        visibleVolumes(value) {
            this.$emit('volume-change', value)
        }
    },

    mounted() {
        this.$emit('volume-change', this.visibleVolumes)
    },

    methods: {
        formatVolume(volume, compact = false) {
            if (volume === undefined) {
                return 'N/A'
            }
            if (volume === null && this.parent === 'builder') {
                volume = 0
            }
            return compact ? formatNumberIntl(volume) : formatNumber(volume)
        },

        contentTooltip(label, volume) {
            return `${label}: ${volume}`
        },
    }
}
</script>

<style lang="scss">
.cell-content-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    .volume-line {
        .volume-text {
            width: 30px;text-align: left;
        }
    }
    .formula-box__legend {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 25px;
        width: 25px;
        border-radius: 30px;
        color: #000 !important;
        svg {
            margin: unset !important;
        }
        svg:not(.icon-small) {
            height: 23px;
            width: 23px;
        }
    }
}
</style>
